import React, { useState, useRef } from 'react';
import './DragDropArea.css';
import useAnalytics from '../hooks/useAnalytics';
import { Upload, Image, Video, Lock } from 'lucide-react';

const DragDropArea = ({ onFileSelect, isCompact, previewUrl, fileType, fileName }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const { trackEvent } = useAnalytics();

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      trackEvent('file_drop', {
        method: 'drag_and_drop',
        file_type: file.type
      });
      onFileSelect(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      trackEvent('file_drop', {
        method: 'file_select',
        file_type: file.type
      });
      onFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleClick = (e) => {
    // Don't open file picker if clicking on video controls
    if (e.target.tagName === 'VIDEO') return;
    fileInputRef.current?.click();
  };

  // Compact mode with preview
  if (isCompact && previewUrl) {
    const isImage = fileType?.startsWith('image/');
    const isVideo = fileType?.startsWith('video/');

    return (
      <div
        className={`drag-drop-area compact ${isDragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <div className="preview-header-compact">
          <div className="preview-title-row">
            <h2>
              {isImage && <><Image size={16} /> Preview</>}
              {isVideo && <><Video size={16} /> Preview</>}
            </h2>
            <span className="drop-hint">Drop to replace</span>
          </div>
          {fileName && <div className="file-name-display">{fileName}</div>}
        </div>
        <div className="preview-content-compact">
          {isImage && (
            <img src={previewUrl} alt="Preview" className="preview-image" />
          )}
          {isVideo && (
            <video controls loop src={previewUrl} className="preview-video">
              Your browser does not support the video tag.
            </video>
          )}
        </div>
        <div className={`drop-overlay ${isDragOver ? 'visible' : ''}`}>
          <Upload size={32} />
          <span>Drop to analyze</span>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,video/*"
          style={{ display: 'none' }}
        />
      </div>
    );
  }

  // Full drop zone mode
  return (
    <div
      className={`drag-drop-area ${isDragOver ? 'drag-over' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
    >
      <div className="drop-icon">
        <Upload size={32} strokeWidth={1.5} />
      </div>
      <p className="drop-title">Drop your file here</p>
      <p className="drop-subtitle">or click to browse</p>
      <div className="supported-formats">
        <span className="format-badge"><Image size={14} /> Images</span>
        <span className="format-badge"><Video size={14} /> Videos</span>
      </div>
      <p className="privacy-note"><Lock size={12} /> All files stay local on your computer</p>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*,video/*"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default DragDropArea;